export const blacklist: Record<string, boolean> = {
  '[Network] Network request failed': true,
  '[Network] RBAC: access denied': true,
};

// Function names injected by in-app browsers (Instagram, Facebook, etc.) whose
// bridge scripts throw unhandled rejections we cannot fix. Used to drop the noise.
export const inAppBrowserInjectedFrames = [
  'dispatchToBridge',
  'sendPostMessage',
  'logLoginFormDetected',
  'mutationObserverCallback',
];

// Browser names Sentry reports for embedded in-app (webview) browsers.
export const inAppBrowserNames = ['Instagram', 'Facebook'];

// Generic WebKit/DOM messages that are only noise when thrown by an in-app
// browser's injected script. Dropped exclusively within an in-app browser
// context so genuine occurrences from our own web code are still reported.
export const inAppBrowserNoiseMessages = [
  'The object does not support the operation or argument.',
  'InvalidAccessError: The object does not support the operation or argument.',
];

export const thirdPartyScriptLoadErrorSources = [
  {
    host: 'www.googletagmanager.com',
    pathnamePrefix: '/gtag/js',
  },
  {
    host: 'cdn.segment.com',
    pathnamePrefix: '/next-integrations/',
  },
];

// React DOM commit-phase frames. A NotFoundError originating here means an
// external actor (browser extension such as Google Translate, in-app browser
// script) mutated a React-managed node, which we cannot fix. Used to drop the
// noise while keeping genuine app-thrown NotFoundErrors that lack these frames.
export const reactReconcilerDomMutationFrames = [
  'commitDeletionEffectsOnFiber',
  'commitMutationEffectsOnFiber',
  'recursivelyTraverseMutationEffects',
];

// Browser-provided (WebKit) DOMException message for the same reconciler-vs-
// external-DOM conflict. Matched alongside the frames because production web
// bundles can minify the React frame names before Sentry symbolicates them,
// whereas this browser message is stable and never minified.
export const reactReconcilerDomNotFoundMessage =
  'The object can not be found here.';

export enum SentryTags {
  BUILD_NUMBER = 'build_number',
  ERROR_TYPE = 'error_type',
  ERROR_SEVERITY = 'error_severity',
  FEATURE_AREA = 'feature_area',
  RESPONSIBLE_TEAM = 'responsible_team',
  FRAUD = 'fraud',
  EXPO_UPDATE_TYPE = 'expo_update_type',
  EXPO_UPDATE_ID = 'expo_update_id',
  EXPO_CHANNEL = 'expo_channel',
  EXPO_RUNTIME_VERSION = 'expo_runtime_version',
}

export const EXPO_UPDATE_CONTEXT_KEY = 'expo_updates';

// Key attached to the Sentry user object carrying the active OTA updateId, so events
// from a device already running a downloaded OTA are attributed to that specific update.
export const SENTRY_OTA_UPDATE_ID_USER_KEY = 'ota_update_id';

export enum ErrorSeverity {
  CRITICAL = 'critical',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export enum FeatureArea {
  AUTHENTICATION = 'authentication',
  PAYMENTS = 'payments',
  FRAUD = 'fraud',
  LENDING = 'lending',
  NOTIFICATIONS = 'notifications',
  DEEP_LINKS = 'deeplinks',
  REWARDS = 'rewards',
  CUSTOMER_SERVICING_SUPPORT = 'customer_servicing_support',
  MY_VARO = 'my_varo',
  CHANGE_PHONE_NUMBER = 'change_phone_number',
  CHANGE_EMAIL = 'change_email',
  CHANGE_PASSWORD = 'change_password',
  PROFILE_PHOTO_UPDATE = 'profile_photo_update',
  EXTERNAL_CARD_DETAILS = 'external_card_details',
  CUSTOMER_ATTRIBUTION = 'customer_attribution',
  LINK_EXTERNAL_DEBIT_CARD_ = 'link_external_debit_card',
  BELIEVE = 'believe',
  ONBOARDING = 'onboarding',
  ONBOARDING_CHECKLIST = 'onboarding_checklist',
  INITIALIZATION = 'initialization',
  PLAID = 'plaid',
  DIRECT_DEPOSIT = 'direct_deposit',
  NETWORKING = 'networking',
}

export enum ResponsibleTeam {
  CUSTOMER_ACQUISITION = 'customer_acquisition',
  FRONTEND_PLATFORM = 'frontend_platform',
  CORE_BANKING = 'core_banking',
  PAYMENTS_TRANSFER = 'payments_transfer',
  LENDING_CREDIT = 'lending_credit',
  CUSTOMER_SUPPORT = 'customer_support',
  FRAUD = 'fraud',
  CASHBACK_REWARDS = 'cashback_rewards',
}
